/**
 * AI Workspace Initialization Overlay
 *
 * Prominent overlay showing schema export progress when an AI terminal is starting.
 * Displays phase, current item, progress bar, and allows dismissal.
 */

import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, X, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import type { TerminalInitState, TerminalInitPhase } from '../../../types/terminal';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type AiInitOverlayProps = {
  sessionId: string;
  initState: TerminalInitState;
  onRetry: () => void;
};

/**
 * Get human-readable phase label
 */
function getPhaseLabel(phase: TerminalInitPhase): string {
  switch (phase) {
    case 'starting':
      return 'Starting';
    case 'checking-counts':
      return 'Checking database size';
    case 'awaiting-filter':
      return 'Waiting for selection';
    case 'context':
      return 'Setting up context';
    case 'tables':
      return 'Exporting tables';
    case 'views':
      return 'Exporting views';
    case 'procedures':
      return 'Exporting procedures';
    case 'functions':
      return 'Exporting functions';
    case 'complete':
      return 'Complete';
    case 'error':
      return 'Error';
    case 'cancelled':
      return 'Cancelled';
    default:
      return phase;
  }
}

/**
 * Calculate overall progress based on phase and phase progress
 */
function calculateOverallProgress(phase: TerminalInitPhase, phaseProgress?: number): number {
  const phaseWeights: Record<TerminalInitPhase, { start: number; weight: number }> = {
    starting: { start: 0, weight: 3 },
    'checking-counts': { start: 3, weight: 2 },
    'awaiting-filter': { start: 5, weight: 0 }, // Paused waiting for user
    context: { start: 5, weight: 5 },
    tables: { start: 10, weight: 40 },
    views: { start: 50, weight: 20 },
    procedures: { start: 70, weight: 15 },
    functions: { start: 85, weight: 15 },
    complete: { start: 100, weight: 0 },
    error: { start: 0, weight: 0 },
    cancelled: { start: 0, weight: 0 },
  };

  const config = phaseWeights[phase];
  if (!config) return 0;

  if (phase === 'complete') return 100;
  if (phase === 'error') return 0;
  if (phase === 'cancelled') return 0;
  if (phase === 'awaiting-filter') return 5; // Static progress while waiting

  const progress = phaseProgress ?? 0;
  return config.start + (config.weight * progress) / 100;
}

/**
 * Prominent overlay component for AI workspace initialization
 */
export function AiInitOverlay({ sessionId, initState, onRetry }: AiInitOverlayProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const isError = initState.phase === 'error';
  const isComplete = initState.phase === 'complete';
  const isCancelled = initState.phase === 'cancelled';
  const isInProgress = !isError && !isComplete && !isCancelled;

  // Reset dismissed state when session changes or when error occurs
  useEffect(() => {
    setIsDismissed(false);
    setShowSuccess(false);
  }, [sessionId]);

  // Show success briefly then auto-dismiss
  useEffect(() => {
    if (isComplete && !isDismissed) {
      setShowSuccess(true);
      const timer = setTimeout(() => {
        setShowSuccess(false);
        setIsDismissed(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isComplete, isDismissed]);

  // Don't render if dismissed (and not error - errors should always show until dismissed)
  if (isDismissed && !isError) {
    return null;
  }

  // If showing success message
  if (showSuccess) {
    return (
      <div
        className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm"
        data-testid="ai-init-overlay"
      >
        <div className="flex items-center gap-3 bg-background border border-border rounded-lg px-6 py-4 shadow-lg">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <span className="text-sm font-medium">AI workspace ready</span>
        </div>
      </div>
    );
  }

  const overallProgress = calculateOverallProgress(initState.phase, initState.progress);

  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      data-testid="ai-init-overlay"
    >
      <div className="w-[400px] max-w-[90%] bg-background border border-border rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            {isError ? (
              <AlertCircle className="h-4 w-4 text-destructive" />
            ) : isCancelled ? (
              <X className="h-4 w-4 text-muted-foreground" />
            ) : (
              <RandomDotmSpinner size={16} className="text-primary" />
            )}
            <span className="text-sm font-medium">
              {isError ? 'Export Failed' : isCancelled ? 'Export cancelled' : 'Setting up AI workspace'}
            </span>
          </div>
          {isInProgress && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => window.electronAPI?.terminal.cancelExport({ sessionId }).catch(() => {})}
                title="Cancel export (the terminal stays available)"
                data-testid="ai-init-cancel-export"
              >
                Cancel export
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsDismissed(true)}
                title="Dismiss (export continues in background)"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-4 py-4 space-y-4">
          {isError ? (
            <>
              <p className="text-sm text-muted-foreground">
                {initState.message || 'Failed to export database schema.'}
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsDismissed(true)}
                >
                  Dismiss
                </Button>
                <Button size="sm" onClick={onRetry}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Retry
                </Button>
              </div>
            </>
          ) : isCancelled ? (
            <>
              <p className="text-sm text-muted-foreground" data-testid="ai-init-cancelled-message">
                Export cancelled — the AI terminal stays available.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsDismissed(true)}
                >
                  Dismiss
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Phase indicator */}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {getPhaseLabel(initState.phase)}
                  </span>
                  <span className="text-muted-foreground">
                    {Math.round(overallProgress)}%
                  </span>
                </div>
                <Progress value={overallProgress} className="h-2" />
              </div>

              {/* Current item */}
              {initState.currentItem && (
                <div className="text-xs text-muted-foreground truncate">
                  {initState.currentItem}
                </div>
              )}

              {/* Help text */}
              <p className="text-xs text-muted-foreground/70">
                Exporting database schema for AI context. You can dismiss this
                overlay and continue using the terminal while export runs in
                background.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
